import { Contact, NewDeal, Organization, Product, User } from 'types'
import { updateContractTimelineData } from 'components/Timeline/createTimelineData'
import { useCreateContract } from 'api/PandaDoc/useCreateContract'
import { useCreateTimeline } from 'api/aws/useCreateTimeline'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useQueryClient } from '@tanstack/react-query'
import { SetStateAction } from 'react'
import { useDealUpdate } from 'api/aws/useEntityUpdate'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useSubmitDealToHeartbeat } from 'api/aws/heartbeat/useSubmitDealToHeartbeat'

export const useCreateDocuments = () => {
  const { mutate: createDocument } = useCreateContract()
  const { deals, products } = useEntitiesFromSearch()
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const { mutate: updateDeal } = useDealUpdate()
  const { syncDealFields } = useSyncQueryCache()
  const { mutateAsync: submitToHeartbeat } = useSubmitDealToHeartbeat()

  const createInternalContract = async (
    setIsCreating: React.Dispatch<SetStateAction<boolean>>,
    newDeal: NewDeal,
    currAuthRecipient: User,
    currContactRecipient: Contact,
    activeUser: User,
    organization: Organization,
    productRows: Product[],
    totals?
  ) => {
    // setIsCreating(true)
    const rawPredecessorDeal = deals?.find(d => d?.deal_uuid === newDeal?.predecessorId) ?? null
    const predecessorProducts = products?.filter(p => p?.deal_uuid === rawPredecessorDeal?.deal_uuid) ?? []
    const predecessorDeal = { ...rawPredecessorDeal, products: predecessorProducts ?? [] }
    const isInternalUpdateRequired = predecessorDeal
      ? newDeal?.status === 'Completed Internally' && rawPredecessorDeal?.dealStatus !== 'Replaced'
      : false

    const cleanedDeal = {
      ...newDeal,
      creation_timestamp: new Date(),
      isCreated: true,
      recipients: {
        authorizedRecipient: newDeal?.recipients?.authorizedRecipient?.map(rec => ({
          ...rec,
          contact_id: currAuthRecipient?.panda_contact_id || currAuthRecipient?.contact_id,
        })),
        contactRecipient: newDeal?.recipients?.contactRecipient?.map(rec => ({
          ...rec,
          contact_id: currContactRecipient?.panda_contact_id || currContactRecipient?.contact_id,
        })),
      },
    }
    console.log('cleanedDeal', cleanedDeal)
    const documentData = {
      deal: cleanedDeal,
      products: productRows,
      organization,
      activeUser,
      ...(isInternalUpdateRequired
        ? { isUpdateRequired: isInternalUpdateRequired, predecessorDeal: predecessorDeal ?? {} }
        : {}),
    }
    console.log('documentData', documentData)

    try {
      const timelineData = updateContractTimelineData(
        cleanedDeal?.deal_uuid,
        activeUser,
        'Create',
        'deals',
        'Contract created'
      )

      // Then, create the PandaDoc document
      const documentData = {
        deal: cleanedDeal,
        products: productRows,
        organization,
        activeUser,
        ...(isInternalUpdateRequired
          ? { isUpdateRequired: isInternalUpdateRequired, predecessorDeal: predecessorDeal ?? {} }
          : {}),
      }

      createDocument(documentData, {
        onSuccess: data => {
          console.log('[createDocument onSuccess] Data', data)
          const contractId = data?.id
          const hasProducts = productRows?.length > 0
          // Update deal with contract ID, draft status, linksGenerated = false
          const dealWithContractId = {
            ...cleanedDeal,
            contractId: contractId,
            status: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.status : 'Created',
            stage: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.stage : 'Contract Created',
            isDraft: true,
            isReverted: false,
            linksGenerated: false,
            lastModified: new Date(),
            ...totals,
            hasProducts: hasProducts,
            products: productRows,
            totals: { ...totals },
          }
          console.log('🟡 Saving deal with contractId and status update:', dealWithContractId)
          // Save again with contractId
          updateDeal(
            {
              PK: dealWithContractId.PK,
              SK: dealWithContractId.SK,
              dealData: dealWithContractId,
            },
            {
              onSuccess: () => {
                setIsCreating(false)
                createTimelineEntry(timelineData)
                syncDealFields(queryClient, cleanedDeal, [
                  { fieldName: 'contractId', newValue: contractId },
                  {
                    fieldName: 'status',
                    newValue: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.status : 'Created',
                  },
                  {
                    fieldName: 'stage',
                    newValue: cleanedDeal?.status === 'Completed Internally' ? cleanedDeal?.stage : 'Contract Created',
                  },
                  { fieldName: 'isDraft', newValue: true },
                  { fieldName: 'isReverted', newValue: false },
                  { fieldName: 'isCreated', newValue: true },
                  { fieldName: 'linksGenerated', newValue: false },
                  { fieldName: 'lastModified', newValue: new Date() },
                  { fieldName: 'totals', newValue: totals },
                  { fieldName: 'hasProducts', newValue: hasProducts },
                ])
                showSnackbar('Document successfully created', 'success')

                // No-signature path only — the deal is now in Deal Won
                // (set when noSignature was toggled) with its contractId
                // saved. Fire the Heartbeat push here because the PandaDoc
                // webhook won't ever trigger for internal-completed deals
                // (no signing event). Signed deals are handled by the
                // backend's PandaDoc webhook → submitDealToHeartbeat path.
                if (cleanedDeal?.noSignature) {
                  pushNoSignatureToHeartbeat(
                    dealWithContractId,
                    contractId,
                    activeUser,
                    submitToHeartbeat,
                    showSnackbar,
                  )
                }
              },
              onError: err => {
                console.error('❌ Failed to update deal with contractId:', err)
                setIsCreating(false)
              },
            }
          )
        },
        onError: err => {
          setIsCreating(false)
          console.error('❌ PandaDoc creation failed:', err)
          showSnackbar('Error creating PandaDoc document', 'error')
        },
      })
    } catch (err) {
      setIsCreating(false)
      console.error('❌ Unexpected error in handleSubmit:', err)
    }
  }
  //   const handleSubmit = async e => {}
  return { createInternalContract }
}

/**
 * Fire the Heartbeat push for a no-signature deal after its contract has
 * been saved with a contractId. Mirrors what the PandaDoc webhook does
 * for signed deals — but kicked off from the FE because internal-
 * completed deals never produce a webhook event.
 *
 * Best-effort: failures show a snackbar but don't fail the
 * create-contract action. Account-creation-from-Grobotic is disabled
 * server-side, so an unlinked org returns skipReason='org_not_linked'
 * and we surface a clear "resolve the link first" message instead of
 * a generic error.
 */
async function pushNoSignatureToHeartbeat(
  deal: { PK?: string; SK?: string; contractId?: string },
  contractId: string,
  activeUser: User | undefined,
  submitToHeartbeat: ReturnType<typeof useSubmitDealToHeartbeat>['mutateAsync'],
  showSnackbar: (msg: string, severity: 'success' | 'error' | 'warning' | 'info') => void,
): Promise<void> {
  if (!deal.PK || !deal.SK) {
    console.warn('[heartbeat] no-signature push skipped — deal missing PK/SK')
    return
  }
  try {
    const result = await submitToHeartbeat({
      PK: deal.PK,
      SK: deal.SK,
      pandaDocId: contractId,
      signedAt: new Date().toISOString(),
      submittedBy: activeUser?.email || activeUser?.name || 'no-signature-create',
      trigger: 'no_signature_flow',
    })
    const r = result.result
    if (r.skipReason === 'org_not_linked') {
      showSnackbar(
        'Heartbeat push skipped — this organization is not linked to a Heartbeat account yet. Resolve the link before pushing.',
        'warning',
      )
      return
    }
    if (r.skipReason === 'invalid_product_pricing') {
      const names = (r.pricingProblems ?? []).map(pp => pp.serviceLine).join(', ')
      showSnackbar(
        `Heartbeat push blocked — ${names || 'a product'} would send $0 of recurring revenue. Set the margin on the deal, then push again.`,
        'error',
      )
      return
    }
    if (!r.success) {
      showSnackbar(`Heartbeat push completed with errors (flow=${r.flow})`, 'error')
      return
    }
    showSnackbar(`Heartbeat push succeeded (flow=${r.flow}, ${r.emittedSubmissions} item(s))`, 'success')
  } catch (err) {
    console.error('[heartbeat] no-signature push failed:', err)
    showSnackbar('Heartbeat push failed — see console for details', 'error')
  }
}
